import { Button, Progress, Tooltip, Typography } from 'antd'
import { CircleAlert, RotateCw, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { formatFileSize } from './attachments'
import type { PendingAttachment } from './attachmentUploads'
import { ChatAttachmentIcon } from './ChatAttachmentIcon'
import { useStyles } from './styles'

const { Text } = Typography

type Props = {
  item: PendingAttachment
  onRemove: (localId: string) => void
  onRetry: (localId: string) => void
}

export const ChatAttachmentChip = ({ item, onRemove, onRetry }: Props) => {
  const { styles, cx } = useStyles()
  const { t } = useTranslation()
  const name = item.file.name
  const failed = item.status === 'failed'
  const failure = item.problem
    ? t(`workspaceHome.attachments.problems.${item.problem}`)
    : item.transient
      ? t('workspaceHome.attachments.uploadFailed')
      : item.error

  return (
    <div
      role="group"
      aria-label={failed && failure ? `${name}: ${failure}` : name}
      className={cx(
        styles.attachmentChip,
        failed && styles.attachmentChipFailed
      )}
    >
      {item.status === 'uploading' ? (
        <Progress
          type="circle"
          percent={item.progress}
          size={14}
          aria-label={t('workspaceHome.attachments.uploading', { name })}
        />
      ) : failed ? (
        <CircleAlert
          size={14}
          strokeWidth={1.5}
          className={styles.attachmentErrorIcon}
          aria-hidden
        />
      ) : (
        <ChatAttachmentIcon
          filename={name}
          mimeType={item.file.type}
          size={14}
          className={styles.attachmentIcon}
        />
      )}
      <Text ellipsis={{ tooltip: name }} className={styles.attachmentName}>
        {name}
      </Text>
      {failed && failure ? (
        <Text
          type="secondary"
          ellipsis={{
            tooltip: item.transient && item.error ? item.error : failure
          }}
          className={styles.attachmentFailure}
        >
          {failure}
        </Text>
      ) : (
        <Text type="secondary" className={styles.attachmentMeta}>
          {formatFileSize(item.file.size)}
        </Text>
      )}
      {failed && !item.problem && !item.rejected ? (
        <Tooltip title={t('workspaceHome.attachments.retry')}>
          <Button
            type="text"
            size="small"
            icon={<RotateCw size={14} strokeWidth={1.5} />}
            aria-label={t('workspaceHome.attachments.retry')}
            onClick={() => onRetry(item.localId)}
          />
        </Tooltip>
      ) : null}
      <Tooltip title={t('workspaceHome.attachments.remove')}>
        <Button
          type="text"
          size="small"
          icon={<X size={14} strokeWidth={1.5} />}
          aria-label={t('workspaceHome.attachments.remove')}
          onClick={() => onRemove(item.localId)}
        />
      </Tooltip>
    </div>
  )
}
